import {
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  type FocusEvent,
} from "react";
import { v4 as uuidv4 } from "uuid";
import { Button, Flex, Text, TextInput, toast } from "@jsq/ds/core";
import { useFormContext } from "@jsq/ds/forms";
import { Icon } from "@jsq/ds/icons";
import { Colors, Sizes } from "@jsq/ds/styles";
import {
  useCreateTaxReportingProfileMutation,
  useDeleteTaxReportingProfileMutation,
  useGetProfilesForControllingPersonsQuery,
  useGetTaxReportingDataQuery,
  useUpdateTaxReportingProfileMutation,
} from "~/graphql/supergraphHooks";
import { getProfileName } from "~/pages/Compliance/CaseDetailsDrawer/Profiles/utils";
import { useCurrentCaseContext } from "~/pages/Compliance/context/CurrentCaseContext";
import { EMPTY_ADDRESS } from "../../constants";
import { useTaxReportingPersistence } from "../../context/TaxReportingPersistenceContext";
import { TAX_REPORTING_TYPE_BY_CONTROLLING_PERSON_FIELD } from "../../taxReportingPersonsFieldTypes";
import {
  controllingPersonEntryToMetadataInput,
  mergeControllingPersonAfterMutation,
  parseCalendarDateString,
} from "../../utils/taxReportingProfileSync";
import { ControllingPersonsForm } from "./ControllingPersonsForm";
import { ControllingPersonsTable } from "./ControllingPersonsTable";
import * as styles from "./styles_vanilla.css";
import type {
  AddressValues,
  ControllingPersonEntry,
  FormFieldConfig,
  SectionActionHandle,
  TaxFormValues,
} from "../../types";

type ProfileSearchResult = {
  id: string;
  name: string;
  firstName: string;
  middleName?: string;
  lastName: string;
  dateOfBirth?: string | null;
  address: AddressValues;
  tin?: string;
  lastUsedAt?: string | null;
};

const taxReportingMutationRefetchQueries = [
  "GetComplianceCase",
  "GetTaxReportingData",
  "GetProfilesForControllingPersons",
];

export type ControllingPersonsSectionHandle = SectionActionHandle;

type ControllingPersonsSectionProps = {
  field: FormFieldConfig;
  readonly?: boolean;
  onAddRef?: React.RefObject<ControllingPersonsSectionHandle | null>;
};

const getEntries = (values: TaxFormValues, fieldName: string): ControllingPersonEntry[] =>
  (values[fieldName] as ControllingPersonEntry[]) || [];

export const ControllingPersonsSection = ({
  field,
  readonly = false,
  onAddRef,
}: ControllingPersonsSectionProps) => {
  const fieldName = field.name;
  const required = field.required ?? false;
  const emptyStateText = field.emptyStateText ?? "There are no controlling persons.";
  const infoText =
    field.infoText ??
    "Edits made here do not affect the source profile. This data must match the physical document 1:1.";
  const addButtonText = field.addButtonText ?? "Add controlling person";
  const updateButtonText = field.updateButtonText ?? "Update controlling person";
  const searchLabel =
    field.searchLabel ?? "Search existing profile or add new controlling person";
  const controllingPersonTypeOptions = field.controllingPersonTypeOptions;
  const showDateOfBirth = field.showDateOfBirth ?? true;
  const controllingPersonTypeLabel = field.controllingPersonTypeLabel;
  const searchInputPlaceholder = field.searchInputPlaceholder ?? "Enter name";
  const searchHelperText =
    field.searchHelperText ??
    "Using an existing profile only pre-fills the information and does not link to the profile used. If the person is not listed, enter details manually.";
  const { values, setFieldValue, errors } = useFormContext<TaxFormValues>();
  const valuesRef = useRef(values);
  valuesRef.current = values;
  const { caseId } = useCurrentCaseContext();
  const taxReportingPersistence = useTaxReportingPersistence();
  const taxReportingType = TAX_REPORTING_TYPE_BY_CONTROLLING_PERSON_FIELD[fieldName];
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [isSearchVisible, setIsSearchVisible] = useState(false);
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const originalEntryRef = useRef<ControllingPersonEntry | null>(null);
  const searchInputWrapperRef = useRef<HTMLDivElement>(null);

  const [createTaxReportingProfile] = useCreateTaxReportingProfileMutation({
    refetchQueries: taxReportingMutationRefetchQueries,
  });
  const [updateTaxReportingProfile] = useUpdateTaxReportingProfileMutation({
    refetchQueries: taxReportingMutationRefetchQueries,
  });
  const [deleteTaxReportingProfile] = useDeleteTaxReportingProfileMutation({
    refetchQueries: taxReportingMutationRefetchQueries,
  });

  const { data: profilesData } = useGetProfilesForControllingPersonsQuery({
    variables: { caseId },
    skip: !caseId,
    fetchPolicy: "cache-first",
  });

  const documentId = taxReportingPersistence?.documentId;
  const { data: taxReportingData } = useGetTaxReportingDataQuery({
    variables: { caseId, documentId: documentId ?? 0 },
    skip: !caseId || !documentId,
    fetchPolicy: "cache-first",
  });

  const allProfiles = useMemo<ProfileSearchResult[]>(() => {
    const results: ProfileSearchResult[] = [];
    const complianceCase = profilesData?.complianceCase;
    if (!complianceCase) return results;

    const mapToSearchResult = (profile: {
      id: number;
      firstName?: string | null;
      middleName?: string | null;
      lastName?: string | null;
      legalName?: string | null;
      dateOfBirth?: string | null;
      taxIdUnencrypted?: string | null;
      lastUsedAt?: string | null;
      addresses?:
        | {
            isPrimaryDomicile?: boolean | null;
            street?: string | null;
            street2?: string | null;
            city?: string | null;
            province?: string | null;
            postalCode?: string | null;
            country?: { alpha2code: string } | null;
          }[]
        | null;
    }): ProfileSearchResult => {
      const primaryAddress =
        profile.addresses?.find((a) => a.isPrimaryDomicile) ?? profile.addresses?.[0];
      const name = getProfileName(profile as Parameters<typeof getProfileName>[0]);
      return {
        id: String(profile.id),
        name,
        firstName: profile.firstName ?? "",
        middleName: profile.middleName ?? "",
        lastName: profile.lastName ?? "",
        dateOfBirth: profile.dateOfBirth ?? null,
        tin: profile.taxIdUnencrypted ?? "",
        lastUsedAt: profile.lastUsedAt ?? null,
        address: {
          country: primaryAddress?.country?.alpha2code ?? "",
          streetLine1: primaryAddress?.street ?? "",
          streetLine2: primaryAddress?.street2 ?? "",
          city: primaryAddress?.city ?? "",
          stateOrProvince: primaryAddress?.province ?? "",
          zipCode: primaryAddress?.postalCode ?? "",
        },
      };
    };

    for (const profile of complianceCase.profiles ?? []) {
      if (profile && profile.complianceObjectType === "individual") {
        results.push(mapToSearchResult(profile));
      }
    }
    return results;
  }, [profilesData]);

  const entries = getEntries(values, fieldName);
  const confirmedEntries = entries.filter((e) => !e._isDraft);
  const fieldError = errors[fieldName];
  const isFormVisible = editingIndex !== null;
  const isActive = isFormVisible || isSearchVisible;

  // Get set of already-selected profile IDs to prevent duplicates
  const selectedProfileIds = useMemo(() => {
    const ids = new Set<number>();

    // Add IDs from draft entries (not yet saved)
    entries.forEach((entry) => {
      if (entry.pendingSourceProfileId) {
        ids.add(entry.pendingSourceProfileId);
      }
    });

    // Add IDs from saved entries (from backend)
    const taxRows = taxReportingData?.taxReportingData ?? [];
    taxRows.forEach((row) => {
      if (row?.profileId && row.taxReportingType === taxReportingType) {
        ids.add(row.profileId);
      }
    });

    return ids;
  }, [entries, taxReportingData?.taxReportingData, taxReportingType]);

  const displayProfiles = useMemo(() => {
    const availableProfiles = allProfiles.filter((p) => !selectedProfileIds.has(Number(p.id)));

    const q = searchQuery.trim().toLowerCase();
    if (!q) return availableProfiles;
    return availableProfiles.filter((p) => p.name.toLowerCase().includes(q));
  }, [allProfiles, searchQuery, selectedProfileIds]);

  const createDraftEntry = useCallback(() => {
    if (readonly) return;
    const draftEntry: ControllingPersonEntry = {
      id: uuidv4(),
      firstName: "",
      middleName: "",
      lastName: "",
      dateOfBirth: null,
      address: { ...EMPTY_ADDRESS },
      tin: "",
      tinNotRequired: false,
      _isDraft: true,
    };
    const newEntries = [...getEntries(values, fieldName), draftEntry];
    originalEntryRef.current = null;
    void setFieldValue(fieldName, newEntries, false);
    setEditingIndex(newEntries.length - 1);
    setIsSearchVisible(false);
    setIsProfileDropdownOpen(false);
    setSearchQuery("");
  }, [fieldName, readonly, setFieldValue, values]);

  const selectProfile = useCallback(
    (profile: ProfileSearchResult) => {
      if (readonly) return;

      // Check if this profile has already been selected
      const profileId = Number(profile.id);
      const currentEntries = getEntries(values, fieldName);

      const isDuplicate = currentEntries.some(
        (entry) => entry.pendingSourceProfileId === profileId,
      );

      if (isDuplicate) {
        toast.error(`${profile.name} has already been added.`);
        return;
      }

      const draftEntry: ControllingPersonEntry = {
        id: uuidv4(),
        pendingSourceProfileId: profileId,
        firstName: profile.firstName,
        middleName: profile.middleName ?? "",
        lastName: profile.lastName,
        dateOfBirth:
          showDateOfBirth && profile.dateOfBirth
            ? parseCalendarDateString(profile.dateOfBirth)
            : null,
        address: { ...profile.address },
        tin: profile.tin ?? "",
        tinNotRequired: false,
        _isDraft: true,
      };
      const newEntries = [...currentEntries, draftEntry];
      originalEntryRef.current = null;
      void setFieldValue(fieldName, newEntries, false);
      setEditingIndex(newEntries.length - 1);
      setIsSearchVisible(false);
      setIsProfileDropdownOpen(false);
      setSearchQuery("");
    },
    [fieldName, readonly, setFieldValue, showDateOfBirth, values],
  );

  const showSearch = useCallback(() => {
    if (readonly || isFormVisible || isSearchVisible) return;
    setIsSearchVisible(true);
  }, [readonly, isFormVisible, isSearchVisible]);

  const hideSearch = useCallback(() => {
    setIsSearchVisible(false);
    setIsProfileDropdownOpen(false);
    setSearchQuery("");
  }, []);

  const handleSearchInputBlur = useCallback((event: FocusEvent<HTMLInputElement>) => {
    const { relatedTarget } = event;
    if (
      relatedTarget instanceof Node &&
      searchInputWrapperRef.current?.contains(relatedTarget)
    ) {
      return;
    }
    setIsProfileDropdownOpen(false);
  }, []);

  useImperativeHandle(onAddRef, () => ({ addEntry: showSearch }), [showSearch]);

  const handleEditClick = useCallback(
    (entry: ControllingPersonEntry) => {
      const currentEntries = getEntries(values, fieldName);
      const index = currentEntries.findIndex((e) => e.id === entry.id);
      if (index !== -1) {
        const entry = currentEntries[index];
        originalEntryRef.current = { ...entry, address: { ...entry.address } };
        setEditingIndex(index);
      }
    },
    [fieldName, values],
  );

  const handleDeleteClick = useCallback(
    async (entryId: string) => {
      const list = getEntries(valuesRef.current, fieldName);
      const entry = list.find((e) => e.id === entryId);
      const taxCtx = taxReportingPersistence;
      const metadataId = entry?.taxReportingMetadataId;
      if (taxCtx != null && taxReportingType != null && !readonly && metadataId != null) {
        try {
          await deleteTaxReportingProfile({
            variables: { metadataId },
          });
        } catch {
          toast.error("Unable to remove this person. Try again.");
          return;
        }
      }

      void setFieldValue(
        fieldName,
        list.filter((e) => e.id !== entryId),
        false,
      );
    },
    [
      deleteTaxReportingProfile,
      fieldName,
      readonly,
      setFieldValue,
      taxReportingPersistence,
      taxReportingType,
    ],
  );

  const handleFormSubmit = useCallback(async () => {
    if (editingIndex === null) return;
    const latestEntries = getEntries(valuesRef.current, fieldName);
    const entry = latestEntries[editingIndex];
    if (!entry) return;
    const entryId = entry.id;

    const taxCtx = taxReportingPersistence;

    let nextEntry: ControllingPersonEntry;

    if (taxCtx != null && taxReportingType != null && !readonly) {
      try {
        const metadata = controllingPersonEntryToMetadataInput(entry);
        if (entry.taxReportingMetadataId) {
          const { data } = await updateTaxReportingProfile({
            variables: { metadataId: entry.taxReportingMetadataId, metadata },
          });
          const row = data?.updateTaxReportingProfile;
          if (!row) {
            toast.error("Unable to update this person. Try again.");
            return;
          }
          nextEntry = mergeControllingPersonAfterMutation(entry, row.id, row.metaData);
        } else {
          const { data } = await createTaxReportingProfile({
            variables: {
              caseId: taxCtx.caseId,
              documentId: taxCtx.documentId,
              parentProfileId: taxCtx.parentProfileId,
              taxReportingType,
              metadata,
              ...(entry.pendingSourceProfileId != null
                ? { sourceProfileId: entry.pendingSourceProfileId }
                : {}),
            },
          });
          const row = data?.createTaxReportingProfile;
          if (!row) {
            toast.error("Unable to save this person. Try again.");
            return;
          }
          nextEntry = mergeControllingPersonAfterMutation(entry, row.id, row.metaData);
        }
      } catch {
        toast.error("Unable to save this person. Try again.");
        return;
      }
    } else {
      const { _isDraft, ...confirmed } = entry;
      nextEntry = confirmed;
    }

    const finalEntries = getEntries(valuesRef.current, fieldName).map((e) =>
      e.id === entryId ? nextEntry : e,
    );
    void setFieldValue(fieldName, finalEntries, false);
    originalEntryRef.current = null;
    setEditingIndex(null);
  }, [
    createTaxReportingProfile,
    editingIndex,
    fieldName,
    readonly,
    setFieldValue,
    taxReportingPersistence,
    taxReportingType,
    updateTaxReportingProfile,
  ]);

  const handleFormCancel = useCallback(() => {
    if (editingIndex === null) return;
    const currentEntries = getEntries(values, fieldName);
    const currentEntry = currentEntries[editingIndex];

    if (currentEntry?._isDraft) {
      void setFieldValue(
        fieldName,
        currentEntries.filter((_, i) => i !== editingIndex),
        false,
      );
    } else if (originalEntryRef.current) {
      void setFieldValue(
        fieldName,
        currentEntries.map((e, i) => (i === editingIndex ? originalEntryRef.current! : e)),
        false,
      );
    }
    originalEntryRef.current = null;
    setEditingIndex(null);
  }, [editingIndex, fieldName, setFieldValue, values]);

  return (
    <Flex
      flexDirection="column"
      gap={Sizes.MD}
      className={styles.container}
      data-testid={`section-${fieldName}`}
    >
      {confirmedEntries.length > 0 && !isActive && (
        <>
          <ControllingPersonsTable
            entries={confirmedEntries}
            onEdit={handleEditClick}
            onDelete={handleDeleteClick}
            readonly={readonly}
          />
          {required && (
            <Text className={styles.requiredText}>
              This is required <span className={styles.requiredAsterisk}>*</span>
            </Text>
          )}
        </>
      )}
      {confirmedEntries.length === 0 && !isActive && (
        <>
          <Text className={styles.emptyStateText}>
            {emptyStateText} {required && <span className={styles.requiredAsterisk}>*</span>}
          </Text>
          {typeof fieldError === "string" && (
            <Flex alignItems="center" gap={Sizes.XS} className={styles.errorMessageContainer}>
              <Icon type="exclamation-circle" color={Colors.Danger600} size="small" />
              <Text className={styles.errorText}>{fieldError}</Text>
            </Flex>
          )}
        </>
      )}
      {isSearchVisible && (
        <div className={styles.searchCard}>
          <Flex justifyContent="space-between" alignItems="center">
            <Text className={styles.searchLabel}>{searchLabel}</Text>
            <Button
              displayAsLink
              onClick={hideSearch}
              data-testid={`${fieldName}-search-cancel`}
            >
              Cancel
            </Button>
          </Flex>
          <div ref={searchInputWrapperRef} className={styles.searchInputWrapper}>
            <TextInput
              placeholder={searchInputPlaceholder}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onFocus={() => setIsProfileDropdownOpen(true)}
              onBlur={handleSearchInputBlur}
              isFullWidth
              data-testid={`${fieldName}-search-input`}
            />
            {isProfileDropdownOpen && (
              <div className={styles.searchDropdown}>
                {displayProfiles.length > 0 && (
                  <>
                    <div className={styles.searchDropdownSectionHeader}>Existing profiles</div>
                    {displayProfiles.map((profile) => (
                      <button
                        type="button"
                        key={profile.id}
                        className={styles.searchDropdownOption}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => selectProfile(profile)}
                        data-testid={`${fieldName}-profile-option-${profile.id}`}
                      >
                        <span className={styles.searchDropdownOptionName}>{profile.name}</span>
                        <span className={styles.searchDropdownOptionMeta}>
                          Individual
                          {profile.lastUsedAt &&
                            !isNaN(new Date(profile.lastUsedAt).getTime()) &&
                            ` | Last used ${new Date(profile.lastUsedAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`}
                        </span>
                      </button>
                    ))}
                    <div className={styles.searchDropdownDivider} />
                  </>
                )}
                <button
                  type="button"
                  className={styles.searchDropdownCreateOption}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={createDraftEntry}
                  data-testid={`${fieldName}-create-new`}
                >
                  + Create new {addButtonText.replace(/^Add\s+/i, "")} profile
                </button>
              </div>
            )}
          </div>
          <Text className={styles.searchHelperText}>{searchHelperText}</Text>
        </div>
      )}
      {editingIndex !== null && (
        <ControllingPersonsForm
          fieldName={fieldName}
          index={editingIndex}
          onSubmit={handleFormSubmit}
          onCancel={handleFormCancel}
          isNewEntry={entries[editingIndex]?._isDraft === true}
          infoText={infoText}
          addButtonText={addButtonText}
          updateButtonText={updateButtonText}
          controllingPersonTypeOptions={controllingPersonTypeOptions}
          showDateOfBirth={showDateOfBirth}
          controllingPersonTypeLabel={controllingPersonTypeLabel}
        />
      )}
    </Flex>
  );
};

export { ControllingPersonsForm } from "./ControllingPersonsForm";
export { ControllingPersonsTable } from "./ControllingPersonsTable";
